import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import type { TplAuditActionsSummary } from '../../services/tplAuditActions';
import { ACTION_STATUS_COLORS } from '../../../../../services/actionService';

interface TplAuditActionsPanelProps {
  summary: TplAuditActionsSummary;
  loading?: boolean;
}

/** Bloco PDCA — ações geradas pela auditoria TPL. */
export const TplAuditActionsPanel: React.FC<TplAuditActionsPanelProps> = ({
  summary,
  loading = false,
}) => {
  const { desviosEncontrados, abertas, vencidas, concluidas, recentActions } = summary;

  const isFullyEmpty =
    !loading &&
    recentActions.length === 0 &&
    desviosEncontrados === 0 &&
    abertas === 0 &&
    concluidas === 0;

  const hasExceptions = recentActions.some(
    (a) => a.overdue || a.noResponsible || a.critical,
  );

  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-800">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-1 dark:border-slate-800">
        <h4 className="text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
          Ações Geradas pela Auditoria
        </h4>
        {!loading && !isFullyEmpty && vencidas > 0 && (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold text-red-700 dark:bg-red-950/30 dark:text-red-300">
            {vencidas} vencida{vencidas > 1 ? 's' : ''}
          </span>
        )}
      </header>

      {loading ? (
        <p className="px-3 py-1.5 text-[10px] text-slate-400">Carregando ações…</p>
      ) : isFullyEmpty ? (
        <p className="px-3 py-1.5 text-[10px] text-slate-400">Nenhuma ação gerada pela auditoria.</p>
      ) : (
        <div className="space-y-1.5 p-2">
          {(desviosEncontrados > 0 || abertas > 0 || concluidas > 0) && (
            <div className="flex flex-wrap gap-2 text-[9px] tabular-nums text-slate-500">
              {desviosEncontrados > 0 && <span>{desviosEncontrados} desvio(s)</span>}
              {abertas > 0 && <span>{abertas} aberta(s)</span>}
              {vencidas > 0 && <span className="font-semibold text-red-600">{vencidas} vencida(s)</span>}
              {concluidas > 0 && <span className="text-emerald-600">{concluidas} concluída(s)</span>}
            </div>
          )}

          {hasExceptions && (
            <div className="flex flex-wrap gap-1">
              {recentActions.some((a) => a.noResponsible) && (
                <ExceptionTag label="Sem responsável" tone="amber" />
              )}
              {recentActions.some((a) => a.critical && a.status !== 'completed') && (
                <ExceptionTag label="Crítica" tone="red" />
              )}
            </div>
          )}

          {recentActions.length === 0 ? (
            <p className="text-[10px] text-slate-400">
              {desviosEncontrados > 0
                ? 'Desvios registrados sem plano de ação vinculado.'
                : 'Nenhuma ação gerada pela auditoria.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px] text-[10px]">
                <thead>
                  <tr className="text-left text-[9px] uppercase text-slate-400">
                    <th className="pb-0.5 pr-2 font-semibold">Origem</th>
                    <th className="pb-0.5 pr-2 font-semibold">Responsável</th>
                    <th className="pb-0.5 pr-2 font-semibold">Prazo</th>
                    <th className="pb-0.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActions.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-t border-slate-100 dark:border-slate-800/60 ${
                        row.overdue
                          ? 'bg-red-50/50 dark:bg-red-950/15'
                          : row.noResponsible
                            ? 'bg-amber-50/40 dark:bg-amber-950/10'
                            : ''
                      }`}
                    >
                      <td className="max-w-[140px] truncate py-0.5 pr-2 font-medium text-slate-800 dark:text-slate-100" title={row.origin}>
                        <span className="inline-flex items-center gap-1">
                          {row.critical && row.status !== 'completed' && (
                            <AlertTriangle size={9} className="shrink-0 text-red-500" aria-hidden />
                          )}
                          {row.origin}
                        </span>
                      </td>
                      <td
                        className={`max-w-[100px] truncate py-0.5 pr-2 ${
                          row.noResponsible ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {row.responsible}
                      </td>
                      <td
                        className={`whitespace-nowrap py-0.5 pr-2 tabular-nums ${
                          row.overdue ? 'font-bold text-red-600 dark:text-red-400' : 'text-slate-500'
                        }`}
                      >
                        {row.overdue && <Clock size={9} className="mr-0.5 inline" aria-hidden />}
                        {row.dueDateLabel}
                      </td>
                      <td className="py-0.5">
                        <span
                          className={`inline-block rounded px-1.5 py-0.5 text-[8px] font-bold ${ACTION_STATUS_COLORS[row.status]}`}
                        >
                          {row.statusLabel}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

function ExceptionTag({ label, tone }: { label: string; tone: 'red' | 'amber' }) {
  const cls =
    tone === 'red'
      ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${cls}`}>{label}</span>
  );
}

export default TplAuditActionsPanel;
